import Image from 'next/image';
import React, { createRef, useContext, useEffect, useState } from 'react';
import { HiOutlineGlobeAlt } from 'react-icons/hi';
import {
  deleteSocialLink,
  postSocialLink,
  putSocialLink,
} from '../../../apis/profile.api';
import {
  handleCheckChangeForm,
  handleCheckFalsyForm,
} from '../../../common/checkForm';
import {
  handleDeleteForm,
  handleResetForm,
  handleSubmitForm,
} from '../../../common/formCallApi';
import {
  checkSocialLinkConvention,
  renderSubmitFormContent,
} from '../../../common/formCommonFunc';
import {
  listIconSocialLink,
  listViewPermissionIcon,
  socialMediaData,
  viewPermissionData,
} from '../../../constant/user-setting-modal-resources';
import { IModalProps as IProps } from '../../../interfaces/components/modal.interface';
import { ISocialLink as ISocialLinkValue } from '../../../interfaces/profile.interface';
import FormLayout from '../../../layouts/FormLayout/FormLayout2';
import AuthContext from '../../../state/auth/AuthContext';
import { useModal } from '../../../state/modal-provider/ModalProvider';
import { initSocialLinkState } from '../../../state/user-setting-modal/initState';
import PrimaryInput from '../../input/PrimaryInput';
import SelectInput from '../../input/SelectInput2';

const requiredKeys = ['socialNetwork'];

const UserSocialLinkModal = ({
  defaultData,
  cancelCallback,
  isModalCancelled,
}: IProps) => {
  const [socialLinkValue, setSocialLinkValue] = useState<ISocialLinkValue>({
    link: defaultData?.link ?? '',
    socialNetwork: socialMediaData[defaultData?.socialNetwork]?.value ?? '',
    viewPermission: defaultData?.viewPermission ?? 'public',
  });

  const [isSubmit, setIsSubmit] = useState<boolean>(false);
  const { setLoading } = useContext(AuthContext);
  const { toast } = useModal();
  const inputLinkRef: React.RefObject<HTMLInputElement> = createRef();

  const handleSetState = (name: string, value: string) => {
    setSocialLinkValue({ ...socialLinkValue, [name]: value });
    setIsSubmit(false);
  };

  const handleToggleLeaveModal = () => {
    if (
      !handleCheckChangeForm(socialLinkValue, {
        link: defaultData?.link ?? '',
        socialNetwork: defaultData?.socialNetwork
          ? socialMediaData[defaultData.socialNetwork + 1].value
          : '',
        viewPermission: defaultData?.viewPermission ?? 'public',
      })
    ) {
      toast.warning({
        deleteTitle: 'Thoát',
        cancelTitle: 'Ở lại',
        onDelete: () => cancelCallback && cancelCallback(false),
        title: 'Thay đổi chưa lưu',
        content:
          'Thay đổi của bạn chưa được lưu. Bạn vẫn muốn thoát mà không lưu?',
      });
    } else {
      cancelCallback && cancelCallback(false);
    }
  };

  const resetForm = () =>
    handleResetForm({
      setValueState: setSocialLinkValue,
      initValueState: initSocialLinkState,
    });

  const deleteForm = () =>
    handleDeleteForm({
      runAsyncFunc: !!defaultData?._id,
      callback: () => setIsSubmit(false),
      asyncFunc: deleteSocialLink,
      handleResetForm: resetForm,
      id: defaultData?._id ?? '',
      formCancelCallBack: cancelCallback,
      toggleSuccessModalNoti: () =>
        toast.success({
          content: 'Xoá thông tin thành công.',
        }),
      toggleErrorModalNoti: () =>
        toast.error({
          content: 'Có lỗi xảy ra, vui lòng thử lại.',
        }),
      setLoading,
    });

  const validateSocialLink = () =>
    checkSocialLinkConvention(
      socialLinkValue.link,
      socialLinkValue.socialNetwork
    );

  const submitForm = () =>
    handleSubmitForm({
      callback: () => setIsSubmit(true),
      extraValidate: validateSocialLink,
      requiredKeys,
      data: socialLinkValue,
      id: defaultData?._id ?? '',
      asyncFunc: !defaultData?._id ? postSocialLink : putSocialLink,
      formCancelCallBack: cancelCallback,
      toggleSuccessModalNoti: () =>
        toast.success({
          content: renderSubmitFormContent(defaultData?._id),
        }),
      toggleErrorModalNoti: () =>
        toast.error({
          content: 'Có lỗi xảy ra, vui lòng thử lại.',
        }),
      setLoading,
    });

  useEffect(() => {
    if (socialLinkValue.socialNetwork !== '') {
      inputLinkRef?.current?.focus();
    }
  }, [socialLinkValue.socialNetwork, inputLinkRef]);

  // warning when leave modal
  useEffect(() => {
    if (isModalCancelled) {
      handleToggleLeaveModal();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isModalCancelled]);

  return (
    <FormLayout
      deleteCallback={() =>
        toast.warning({
          title: 'Xoá mục liên kết xã hội!',
          content: 'Bạn chắc chắn muốn xoá nội dung mục liên kết xã hội?',
          deleteTitle: 'Xoá',
          cancelTitle: 'Huỷ',
          onDelete: deleteForm,
        })
      }
      saveCallback={submitForm}
      formName="liên kết"
      cancelCallback={handleToggleLeaveModal}
      disabledDeletButton={handleCheckFalsyForm(socialLinkValue)}
    >
      <div className="sm:py-4 flex flex-col justify-center gap-7">
        <SelectInput
          data={socialMediaData}
          propertyName="socialNetwork"
          placeholder="Mạng xã hội*"
          error={isSubmit && !socialLinkValue.socialNetwork}
          value={socialLinkValue.socialNetwork}
          setValue={handleSetState}
          isObject
          placeholderIcon={
            <Image
              alt=""
              priority
              className=""
              src={`/icons/SocialLinks.svg`}
              width={30}
              height={30}
            />
          }
          listIcon={listIconSocialLink}
          notReactIcon
        />
        {isSubmit && !socialLinkValue.socialNetwork && (
          <p className="text-red-400 -mt-5 -mb-3 text-sm">
            Vui lòng chọn mạng xã hội
          </p>
        )}
        {socialLinkValue.socialNetwork && (
          <span className="text-[#979797] font-normal text-base -mb-5 -mt-1">
            Dán link đường dẫn đến trang{' '}
            {socialLinkValue.socialNetwork.charAt(0).toUpperCase() +
              socialLinkValue.socialNetwork.slice(1)}{' '}
            cá nhân của bạn vào đây
          </span>
        )}
        <PrimaryInput
          type="text"
          desc="Liên kết đến trang cá nhân (URL)"
          value={socialLinkValue.link}
          setValue={handleSetState}
          isObject
          inputName="link"
          icon={<HiOutlineGlobeAlt />}
          disabled={socialLinkValue.socialNetwork === '' ? true : false}
          currentRef={inputLinkRef}
          error={
            isSubmit && socialLinkValue.socialNetwork && !validateSocialLink()
          }
        />
        {socialLinkValue.socialNetwork && isSubmit && (
          <>
            {!socialLinkValue.link ? (
              <p className="text-red-400 -mt-5 -mb-3 text-sm">
                Vui lòng nhập địa chỉ mạng xã hội
              </p>
            ) : (
              !validateSocialLink() && (
                <p className="text-red-400 -mt-5 -mb-3 text-sm">
                  Địa chỉ mạng xã hội không khớp với mạng xã hội bạn chọn, vui
                  lòng thử lại
                </p>
              )
            )}
          </>
        )}
        <SelectInput
          data={viewPermissionData}
          propertyName="viewPermission"
          error={false}
          value={socialLinkValue.viewPermission}
          setValue={handleSetState}
          isObject
          listIcon={listViewPermissionIcon}
        />
      </div>
    </FormLayout>
  );
};

export default UserSocialLinkModal;
